import { useState, useEffect } from 'react';

import { useConfigValue } from '@/components/providers/config-provider';
import { checkAdapterSupport } from '@/lib/dictation/adapter-loader';
import type { DictationProvider } from '@/lib/dictation/types';
import type { SttConfig } from '@/lib/types/tenant';

export type { DictationProvider };

interface UseDictationSupportResult {
  isSupported: boolean;
  isCheckingSupport: boolean;
  enableDictation: boolean;
  dictationProvider: DictationProvider;
  showDictation: boolean;
}

/**
 * Hook to check if dictation is enabled and supported for the current tenant.
 * Lazy loads the adapter module only when dictation is enabled.
 */
export function useDictationSupport(): UseDictationSupportResult {
  const sttConfig = useConfigValue<SttConfig>('meta.stt');
  const enableDictation = sttConfig?.enabled ?? false;
  const dictationProvider = sttConfig?.provider ?? 'browser';
  const [hasChecked, setHasChecked] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    if (!enableDictation) return;

    checkAdapterSupport(dictationProvider).then(supported => {
      setIsSupported(supported);
      setHasChecked(true);
    });
  }, [enableDictation, dictationProvider]);

  const isCheckingSupport = enableDictation && !hasChecked;

  return {
    isSupported,
    isCheckingSupport,
    enableDictation,
    dictationProvider,
    showDictation: enableDictation && (isSupported || isCheckingSupport),
  };
}
